import type { MailboxInfo } from 'owa-client-types';
import { action, orchestrator } from 'satcheljs';
import {
    getCoprincipalAccounts,
    ContractsSupportedFilter,
    StartupFilter,
} from 'owa-account-source-list-store';
import { DatapointStatus, logUsage, PerformanceDatapoint } from 'owa-analytics';
import { M365AcquisitionsDocument } from '../graphql/query/__generated__/M365Acquisitions.interface';
import { query } from 'owa-apollo';
import { getFeatureFlagsIndexerForMailboxInfo, isFeatureEnabled } from 'owa-feature-flags';
import type { M365Acquisition, M365AcquisitionsQuery } from '../types';
import type { ApolloQueryResult } from '@apollo/client';
import { addTimingsToGQLQuery } from 'owa-analytics-shared';
import type { GQLWaterfallContext } from 'owa-analytics-types';
import { getModuleContextMailboxInfo } from 'owa-module-context-mailboxinfo';

// Global timer reference for MOS3 polling.
let timerRef: ReturnType<typeof setTimeout> | undefined;
const OWA_STEADY_STATE_INTERVAL_IN_SEC = 14400; // 4 hours
export interface OnAcquisitionsRemoteRefreshActionMessage {
    nextInterval: number;
    m365AcquisitionsEdges: M365Acquisition[];
    isInitializationFetch: boolean;
    mailboxInfo: MailboxInfo | undefined;
}
const onAcquisitionsRemoteRefresh: (
    nextInterval: number,
    m365AcquisitionsEdges: M365Acquisition[],
    isInitializationFetch: boolean,
    mailboxInfo?: MailboxInfo
) => OnAcquisitionsRemoteRefreshActionMessage = action(
    'onAcquisitionsRemoteRefresh',
    (
        nextInterval: number,
        m365AcquisitionsEdges: M365Acquisition[],
        isInitializationFetch: boolean,
        mailboxInfo?: MailboxInfo
    ): OnAcquisitionsRemoteRefreshActionMessage => ({
        nextInterval,
        m365AcquisitionsEdges,
        isInitializationFetch,
        mailboxInfo,
    })
);

orchestrator(onAcquisitionsRemoteRefresh, actionMessage => {
    const initialTimer = actionMessage.nextInterval;
    const mailboxInfo = actionMessage.mailboxInfo;
    if (timerRef) {
        clearTimeout(timerRef);
    }
    const refreshFunction = async () => {
        const queryResult = await dispatchNetworkOnlyQueryToApollo(mailboxInfo);
        const newIntervalInSec = queryResult.data.m365Acquisitions?.nextInterval;

        if (newIntervalInSec == 0) {
            /*
             * Refresh interval should never be 0. OWS` has logic to handle this case,
             * which indicates that MOS3 has paginated results. Prime will repeatedly call MOS3
             * and only return when the interval is >0 (meaning we've fetched all apps).
             */

            logUsage(
                'M365AcquisitionsQueryWeb_RefreshIntervalZero',
                mailboxInfo ?? getModuleContextMailboxInfo()
            );
        }
        const nextIntervalInSec = getOwaIntervalInSeconds(newIntervalInSec);
        timerRef = setTimeout(refreshFunction, nextIntervalInSec * 1000);
    };
    timerRef = setTimeout(refreshFunction, getOwaIntervalInSeconds(initialTimer) * 1000);
});

function dispatchNetworkOnlyQueryToApollo(
    mailboxInfo?: MailboxInfo
): Promise<ApolloQueryResult<M365AcquisitionsQuery>> {
    if (timerRef) {
        clearTimeout(timerRef);
    }
    let queryContext = {
        coprincipalAccountSourceIds: getCoprincipalAccounts(
            StartupFilter.StartingOrCompleteOrError,
            ContractsSupportedFilter.Any
        ).map(account => account.sourceId),
        featureFlagIndexer: getFeatureFlagsIndexerForMailboxInfo(mailboxInfo),
    };

    const runQuery = () =>
        query(M365AcquisitionsDocument, {
            fetchPolicy: 'network-only',
            errorPolicy: 'all',
            variables: {
                mailboxInfo,
            },
            context: queryContext,
        });

    if (mailboxInfo) {
        const dp = new PerformanceDatapoint('AcquisitionsRemoteRefreshQuery', mailboxInfo);
        return addTimingsToGQLQuery(
            dp,
            { isEnabled: isFeatureEnabled('mos-acqwaterfall') },
            (wfContext: GQLWaterfallContext) => {
                queryContext = Object.assign(queryContext, wfContext);
                return runQuery();
            }
        )
            .then(result => {
                dp.end();
                return result;
            })
            .catch(error => {
                dp.endWithError(DatapointStatus.ServerError, error);
                throw error;
            });
    }
    return runQuery();
}

function getOwaIntervalInSeconds(nextInterval: number) {
    return nextInterval >= 1800 || nextInterval <= 0
        ? OWA_STEADY_STATE_INTERVAL_IN_SEC
        : nextInterval;
}

export default onAcquisitionsRemoteRefresh;
